import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Tag, MapPin, ChevronDown } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ProfissionalCard } from '../components/ProfissionalCard';
import { TickerPromocoes } from '../components/TickerPromocoes';
import { SlideAnuncios } from '../components/BannerAnuncios';
import { GradeAnuncios } from '../components/GradeAnuncios';
import { SeletorCidade } from '../components/SeletorCidade';
import { useCidade } from '../context/CidadeContext';
import api from '../lib/api';

const CATEGORIAS = [
  'Arquiteto', 'Azulejista', 'Carpinteiro', 'Chaveiro', 'Dedetizador',
  'Designer de interiores', 'Eletricista', 'Encanador', 'Gesseiro',
  'Jardineiro', 'Marceneiro', 'Mestre de obras', 'Paisagista', 'Pedreiro',
  'Pintor', 'Serralheiro', 'Servente de obra',
];

function SkeletonCard() {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm animate-pulse">
      <div className="flex gap-4">
        <div className="w-16 h-16 bg-gray-200 rounded-2xl" />
        <div className="flex-1 space-y-2.5">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-200 rounded w-1/2" />
          <div className="h-3 bg-gray-200 rounded w-2/3" />
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [busca, setBusca] = useState('');
  const [categoriaHome, setCategoriaHome] = useState('');
  const [categoriasLista, setCategoriasLista] = useState([]);
  const [profissionais, setProfissionais] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCidadeModal, setShowCidadeModal] = useState(false);
  const { cidade, loaded } = useCidade();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/categorias').then(r => setCategoriasLista(r.data.map(c => c.nome))).catch(() => {});
  }, []);

  useEffect(() => {
    if (loaded && !cidade && !localStorage.getItem('guiacit-city-skipped')) {
      setTimeout(() => setShowCidadeModal(true), 800);
    }
  }, [loaded, cidade]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 6 });
    if (cidade) { params.set('cidade', cidade.nome); params.set('estado', cidade.uf); }

    Promise.all([
      api.get(`/profissionais?${params}`),
      api.get('/profissionais?limit=1'),
    ]).then(([res, totalRes]) => {
      setProfissionais(res.data.profissionais || []);
      setTotal(totalRes.data.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [cidade]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (busca.trim()) params.set('q', busca.trim());
    if (categoriaHome) params.set('categoria', categoriaHome);
    if (cidade) { params.set('cidade', cidade.nome); params.set('estado', cidade.uf); }
    navigate(`/buscar${params.toString() ? '?' + params.toString() : ''}`);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <TickerPromocoes />

      {/* === BANNER SECTION: Slider + Grade cards (fundo verde, igual ao original) === */}
      <section style={{ background: '#BCFFBA' }} className="px-0 sm:px-4 pt-0 sm:pt-5 pb-6">
        <div className="max-w-6xl mx-auto space-y-3">
          {/* Slider grande (R$79/7 dias) */}
          <SlideAnuncios />

          {/* Grade de cards (R$39/7 dias) */}
          <div className="px-4 sm:px-0">
            <GradeAnuncios />
          </div>
        </div>
      </section>

      {/* === SEARCH HERO === */}
      <section className="bg-white py-10 px-4 border-b border-gray-100">
        <div className="max-w-3xl mx-auto text-center">
          <h1
            className="text-4xl sm:text-5xl font-bold text-[#085041] mb-3 tracking-tight"
            style={{ fontFamily: "'Work Sans', sans-serif" }}
          >
            Encontre o profissional certo
          </h1>
          <p className="text-gray-500 text-base mb-2">
            Pedreiros, eletricistas, encanadores e mais — todos verificados, com portfólio real.
          </p>
          {cidade ? (
            <p className="text-[#1D9E75] text-sm font-medium mb-6 flex items-center justify-center gap-1.5">
              <MapPin size={14} />
              Mostrando profissionais em {cidade.nome}/{cidade.uf}
            </p>
          ) : (
            <p className="text-[#1D9E75] text-sm font-medium mb-6">{total}+ profissionais cadastrados</p>
          )}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto space-y-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                  placeholder="Ex: eletricista, pedreiro, pintor..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 pl-11 text-base focus:outline-none focus:ring-2 focus:ring-[#1D9E75] focus:border-transparent bg-white"
                  data-testid="hero-search-input"
                />
              </div>
              <button type="submit" className="btn-primary px-6 py-3 text-base" data-testid="hero-search-btn">
                Buscar
              </button>
            </div>

            <div className="relative">
              <ChevronDown size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={categoriaHome}
                onChange={e => {
                  const cat = e.target.value;
                  setCategoriaHome(cat);
                  if (cat) {
                    const params = new URLSearchParams();
                    params.set('categoria', cat);
                    if (cidade) { params.set('cidade', cidade.nome); params.set('estado', cidade.uf); }
                    navigate(`/buscar?${params.toString()}`);
                  }
                }}
                data-testid="hero-categoria-select"
                className="w-full appearance-none border border-gray-200 rounded-xl px-4 py-3 pr-10 text-base focus:outline-none focus:ring-2 focus:ring-[#1D9E75] bg-white text-gray-500"
              >
                <option value="">Selecione a categoria do profissional…</option>
                {categoriasLista.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </form>
        </div>
      </section>

      {/* === PROMOÇÕES CTA === */}
      <div className="bg-white px-4 pt-6 pb-2 flex justify-center">
        <Link
          to="/promocoes"
          className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-2xl transition-colors text-base shadow-sm"
          data-testid="promocoes-btn"
        >
          <Tag size={18} />
          Promoções do Mês
        </Link>
      </div>

      {/* === CATEGORIAS === */}
      <section className="py-10 px-4 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-gray-400 text-xs font-medium uppercase tracking-widest mb-5">Buscar por categoria</p>
          <div className="flex flex-wrap justify-center gap-2">
            {CATEGORIAS.map(c => (
              <Link
                key={c}
                to={`/buscar?q=${encodeURIComponent(c)}${cidade ? `&cidade=${cidade.nome}&estado=${cidade.uf}` : ''}`}
                className="text-sm text-gray-600 border border-gray-200 hover:border-[#1D9E75] hover:text-[#1D9E75] hover:bg-[#E1F5EE] px-4 py-2 rounded-full transition-colors"
                data-testid={`cat-${c.toLowerCase().replace(/[\s/]+/g, '-')}`}
              >
                {c}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* === PROFISSIONAIS EM DESTAQUE === */}
      <section className="py-14 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-[#085041]" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                Profissionais em destaque
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                {cidade ? `Verificados em ${cidade.nome}` : 'Verificados e com boa avaliação'}
              </p>
            </div>
            <Link to="/buscar" className="text-[#1D9E75] text-sm font-medium hover:text-[#0F6E56] flex items-center gap-1 transition-colors">
              Ver todos <ArrowRight size={14} />
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : profissionais.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-xl mb-2">Nenhum profissional encontrado</p>
              {cidade && <p className="text-sm">Tente buscar em todas as cidades.</p>}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profissionais.map(p => <ProfissionalCard key={p.id} profissional={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* === COMO FUNCIONA (busca GRATUITA) === */}
      <section className="py-14 px-4 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-[#085041] mb-12" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            Como funciona
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { n: '1', t: 'Busque gratuitamente', d: 'Encontre profissionais, veja o portfólio completo, fotos de obras realizadas e avaliações de clientes — 100% gratuito.' },
              { n: '2', t: 'Entre em contato', d: 'Veja o WhatsApp e telefone do profissional direto na plataforma, sem custo adicional.' },
              { n: '3', t: 'Contrate com segurança', d: 'Combine o serviço diretamente com o profissional e, depois, avalie sua experiência.' },
            ].map(s => (
              <div key={s.n} className="text-center">
                <div className="w-12 h-12 bg-[#E1F5EE] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#1D9E75] font-bold text-lg">{s.n}</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{s.t}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 bg-[#E1F5EE] rounded-2xl p-8 text-center">
            <p className="text-[#085041] font-bold text-xl mb-2">Acesso 100% gratuito</p>
            <p className="text-[#1D9E75] text-sm mb-6">Busque e entre em contato com profissionais sem pagar nada</p>
            <Link to="/buscar" className="btn-primary" data-testid="cta-buscar-btn">
              Encontrar profissional agora
            </Link>
          </div>
        </div>
      </section>

      {/* === PARA PROFISSIONAIS === */}
      <section className="py-14 px-4 bg-[#085041] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            É profissional de serviços?
          </h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto text-sm leading-relaxed">
            Crie seu perfil digital, receba clientes pela plataforma e tenha um link personalizado para compartilhar.
            <strong className="text-white block mt-1">Assinatura anual — pague 1x por ano.</strong>
          </p>
          <div className="grid sm:grid-cols-3 gap-5 mb-8 text-left">
            {[
              { t: 'Portfólio com fotos', d: 'Mostre seus trabalhos com fotos de qualidade.' },
              { t: 'Link personalizado', d: 'Compartilhe no WhatsApp, cartão e redes sociais.' },
              { t: 'Avaliações reais', d: 'Clientes avaliam, construindo sua reputação.' },
            ].map(f => (
              <div key={f.t} className="bg-white/10 rounded-2xl p-5">
                <h4 className="font-semibold mb-2 text-sm">{f.t}</h4>
                <p className="text-white/60 text-xs leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
          <Link
            to="/cadastro"
            className="inline-block bg-[#1D9E75] hover:bg-[#0F6E56] text-white font-semibold py-3 px-8 rounded-xl transition-colors"
            data-testid="cta-cadastro-btn"
          >
            Cadastrar meu perfil — Assinatura anual
          </Link>
        </div>
      </section>

      {/* === PARA ANUNCIANTES === */}
      <section className="py-10 px-4 bg-amber-50 border-t border-amber-100">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-6">
            <div>
              <h2 className="text-xl font-bold text-amber-900" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                Vende materiais ou ferramentas?
              </h2>
              <p className="text-amber-700 text-sm mt-1">Anuncie para profissionais e clientes que buscam serviços agora.</p>
            </div>
            <Link
              to="/anunciante/cadastro"
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-8 rounded-xl whitespace-nowrap transition-colors"
              data-testid="cta-anunciante-btn"
            >
              Quero anunciar
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl p-5 border border-amber-100 shadow-sm">
              <p className="font-semibold text-amber-900 mb-1">Anúncio Slider (destaque)</p>
              <p className="text-2xl font-bold text-amber-700">R$79 <span className="text-base font-normal text-amber-600">/ 7 dias</span></p>
              <p className="text-amber-600 text-xs mt-2">Exibido no banner principal. Renovável ao fim do período.</p>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-amber-100 shadow-sm">
              <p className="font-semibold text-amber-900 mb-1">Anúncio Card (grade)</p>
              <p className="text-2xl font-bold text-amber-700">R$39 <span className="text-base font-normal text-amber-600">/ 7 dias</span></p>
              <p className="text-amber-600 text-xs mt-2">Exibido na grade abaixo do slider. Renovável ao fim do período.</p>
            </div>
          </div>
        </div>
      </section>

      {/* === SOBRA DE OBRAS === */}
      <section className="py-10 px-4 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Work Sans', sans-serif" }}>
              Tem material sobrando de obra?
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Venda na plataforma — cobramos apenas <strong className="text-[#1D9E75]">20% do valor de venda</strong> como comissão.
            </p>
          </div>
          <Link
            to="/sobra-de-obras"
            className="border-2 border-[#1D9E75] text-[#1D9E75] hover:bg-[#E1F5EE] font-semibold py-3 px-8 rounded-xl whitespace-nowrap transition-colors"
            data-testid="cta-sobra-obras-btn"
          >
            Anunciar material
          </Link>
        </div>
      </section>

      <Footer />

      {showCidadeModal && (
        <SeletorCidade onClose={() => {
          setShowCidadeModal(false);
          localStorage.setItem('guiacit-city-skipped', '1');
        }} />
      )}
    </div>
  );
}
